import React from 'react';
import { observer } from 'mobx-react-lite';
import { Line, LineChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { apiFetch } from '../../api/apiClient';
import { getAPIUrl } from '../../api/apiUtils';
import ChartPointDataModal from '../../components/ChartPointDataModal';
import { groupIdToTitle } from '../../utils/groupTitles';

const DAY_MS = 24 * 60 * 60 * 1000;

function fillDays(startDate, endDate, rows) {
  const byDate = new Map(rows.map((row) => [row.date, row]));
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return [];
  const result = [];
  for (let timestamp = start; timestamp <= end; timestamp += DAY_MS) {
    const date = new Date(timestamp).toISOString().slice(0, 10);
    result.push({ date, timestamp, ...(byDate.get(date) || {}) });
  }
  return result;
}

const formatDay = (timestamp) => new Date(timestamp).toLocaleDateString('ru-RU', { timeZone: 'UTC' });

function DailyChart({ title, description, data, loading, error, children }) {
  return (
    <section style={{ marginTop: 28 }}>
      <h3>{title}</h3>
      <p className="text-muted small">{description}</p>
      {error ? (
        <div className="text-danger">{error}</div>
      ) : loading ? (
        <div className="text-muted">Данные загружаются...</div>
      ) : !data.length ? (
        <div className="text-muted">Нет данных для отображения</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 15, right: 30, left: 5, bottom: 25 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={formatDay} minTickGap={40} />
            <YAxis yAxisId="count" allowDecimals={false} />
            <Tooltip labelFormatter={formatDay} />
            <Legend verticalAlign="top" height={32} />
            {children}
          </LineChart>
        </ResponsiveContainer>
      )}
    </section>
  );
}

const DynamicsContextCharts = observer(() => {
  const filters = useGlobalFilters();
  const filtersKey = filters.filtersString;
  const [outliers, setOutliers] = React.useState([]);
  const [eligibleAuthors, setEligibleAuthors] = React.useState(0);
  const [actionOutliers, setActionOutliers] = React.useState([]);
  const [eligibleActionAuthors, setEligibleActionAuthors] = React.useState(0);
  const [activity, setActivity] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [details, setDetails] = React.useState(null);
  const detailsRequest = React.useRef(null);

  const closeDetails = () => {
    detailsRequest.current?.abort();
    setDetails(null);
  };

  const openDetails = async (chart, point) => {
    if (!point?.date || (chart !== 'activity' ? !point.authors : !(point.posts || point.comments || point.likes))) return;
    detailsRequest.current?.abort();
    const controller = new AbortController();
    detailsRequest.current = controller;
    setDetails({ date: point.date, chart, rows: [], loading: true });
    const params = new URLSearchParams({
      date: point.date,
      group_ids: filters.selectedGroups.join(','),
    });
    if (chart !== 'activity') {
      params.set('start_date', filters.startDate);
      params.set('end_date', filters.endDate);
      params.set('metric', chart === 'action' ? 'verbs_per_nouns_coef' : 'trager_coef');
    }
    const path = chart !== 'activity'
      ? '/detection/user_marker_outliers_daily/details'
      : '/vk/activity/daily/details';
    try {
      const response = await apiFetch(`${getAPIUrl()}${path}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (!controller.signal.aborted) setDetails({
        date: point.date,
        chart,
        rows: (result.data || []).map((row) => ({
          ...row,
          Группа: groupIdToTitle[String(row.Группа)] || row.Группа,
        })),
        loading: false,
      });
    } catch (error) {
      if (!controller.signal.aborted) setDetails({ date: point.date, chart, rows: [], loading: false, error: `Не удалось загрузить данные: ${error.message}` });
    }
  };

  const pointHandler = (chart) => ({
    r: 6,
    cursor: 'pointer',
    onMouseDown: (_event, dot) => openDetails(chart, dot.payload),
  });

  React.useEffect(() => {
    const controller = new AbortController();
    detailsRequest.current?.abort();
    setDetails(null);
    const { startDate, endDate, selectedGroups } = filters;
    if (!startDate || !endDate || !selectedGroups.length) {
      setOutliers([]);
      setEligibleAuthors(0);
      setActionOutliers([]);
      setEligibleActionAuthors(0);
      setActivity([]);
      setErrors({});
      setLoading(false);
      return () => controller.abort();
    }

    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_ids: selectedGroups.join(','),
    });
    const load = async (path, metric) => {
      const query = new URLSearchParams(params);
      if (metric) query.set('metric', metric);
      const response = await apiFetch(`${getAPIUrl()}${path}?${query}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    };

    setLoading(true);
    setErrors({});
    const settle = (promise) => promise.then(
      (value) => ({ status: 'fulfilled', value }),
      (reason) => ({ status: 'rejected', reason }),
    );
    const loadOutliers = async () => {
      const tragerResult = await settle(load('/detection/user_marker_outliers_daily', 'trager_coef'));
      const actionResult = await settle(load('/detection/user_marker_outliers_daily', 'verbs_per_nouns_coef'));
      return [tragerResult, actionResult];
    };
    Promise.all([
      loadOutliers(),
      settle(load('/vk/activity/daily')),
    ]).then(([[outlierResult, actionResult], activityResult]) => {
      if (controller.signal.aborted) return;
      const outlierResponse = outlierResult.status === 'fulfilled' ? outlierResult.value : null;
      const actionResponse = actionResult.status === 'fulfilled' ? actionResult.value : null;
      const activityRows = activityResult.status === 'fulfilled' ? activityResult.value.data || [] : [];
      setEligibleAuthors(outlierResponse?.eligible_authors || 0);
      setOutliers(outlierResponse?.eligible_authors ? fillDays(startDate, endDate, outlierResponse.data || []).map((row) => ({ ...row, authors: row.authors || 0 })) : []);
      setEligibleActionAuthors(actionResponse?.eligible_authors || 0);
      setActionOutliers(actionResponse?.eligible_authors ? fillDays(startDate, endDate, actionResponse.data || []).map((row) => ({ ...row, authors: row.authors || 0 })) : []);
      setActivity(activityRows.length ? fillDays(startDate, endDate, activityRows).map((row) => ({ ...row, posts: row.posts || 0, comments: row.comments || 0, likes: row.likes || 0 })) : []);
      setErrors({
        outliers: outlierResult.status === 'rejected' ? outlierResult.reason.message : null,
        action: actionResult.status === 'rejected' ? actionResult.reason.message : null,
        activity: activityResult.status === 'rejected' ? activityResult.reason.message : null,
      });
      setLoading(false);
    });
    return () => controller.abort();
    // filtersKey changes when the selected dates or groups change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  if (!filters.selectedGroups.length) return null;
  return (
    <>
      <DailyChart
        title="Авторы с отклонением коэффициента Трейгера"
        description={`Число авторов за день, у которых средний коэффициент выше личного среднего за выбранный диапазон дат +1σ или ниже среднего −1σ. Каждый автор считается один раз. Учитываются авторы хотя бы с одним комментарием или ответом длиной от 100 символов. Значения выше 100 исключены. Подходящих авторов: ${eligibleAuthors}. Нажмите на точку, чтобы увидеть комментарии и ответы за соответствующим порогом, сгруппированные по постам.`}
        data={outliers}
        loading={loading}
        error={errors.outliers}
      >
        <Line yAxisId="count" type="linear" dataKey="authors" name="Авторов" stroke="#b91c1c" strokeWidth={2} dot={false} activeDot={pointHandler('trager')} />
      </DailyChart>
      <DailyChart
        title="Авторы с отклонением коэффициента опредмеченности действия"
        description={`Число авторов за день, у которых средний коэффициент опредмеченности действия вышел за личный диапазон ±1σ, рассчитанный по выбранным датам. Учитываются комментарии и ответы длиной от 100 символов, значения коэффициента выше 100 исключены. Подходящих авторов: ${eligibleActionAuthors}. Нажмите на точку, чтобы увидеть комментарии и ответы за соответствующим порогом, сгруппированные по постам.`}
        data={actionOutliers}
        loading={loading}
        error={errors.action}
      >
        <Line yAxisId="count" type="linear" dataKey="authors" name="Авторов" stroke="#7c3aed" strokeWidth={2} dot={false} activeDot={pointHandler('action')} />
      </DailyChart>
      <DailyChart
        title="Активность в сообществах по дням"
        description="Посты и комментарии, включая ответы, считаются по дате создания. Лайки — текущее число лайков у постов, опубликованных в этот день; дата постановки лайка неизвестна. Нажмите на точку любой линии, чтобы увидеть посты за день."
        data={activity}
        loading={loading}
        error={errors.activity}
      >
        <YAxis yAxisId="likes" orientation="right" allowDecimals={false} />
        <Line yAxisId="count" type="linear" dataKey="posts" name="Посты" stroke="#2563eb" strokeWidth={2} dot={false} activeDot={pointHandler('activity')} />
        <Line yAxisId="count" type="linear" dataKey="comments" name="Комментарии" stroke="#16a34a" strokeWidth={2} dot={false} activeDot={pointHandler('activity')} />
        <Line yAxisId="likes" type="linear" dataKey="likes" name="Лайки на постах" stroke="#d97706" strokeWidth={2} dot={false} activeDot={pointHandler('activity')} />
      </DailyChart>
      {details && <ChartPointDataModal date={details.date} rows={details.rows} loading={details.loading} error={details.error} groupByPost={details.chart !== 'activity'} onClose={closeDetails} />}
    </>
  );
});

export default DynamicsContextCharts;
